import Remove from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { WarningIcon } from '@phosphor-icons/react/Warning';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../graphql/fragment/audiences/ListCountries.generated.tsx';
import type { ListRegionsQuery } from '../../../../../../../../graphql/fragment/audiences/ListRegions.generated.tsx';
import type { FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { daysToExpiration } from '../../../../../../utils/daysToExpiration.ts';
import { isSegmentExpired, isSegmentSoonToExpire } from '../../../../../utils/hasExpiredSegments.ts';
import { SegmentCategory } from '../../../hooks/types.ts';
import { useReferenceData } from '../../../hooks/useReferenceData.ts';
import { BasicInfo } from '../../drawer/components/SegmentListItem/BasicInfo.tsx';
import { MarketMetadata } from '../../drawer/components/SegmentListItem/MarketMetadata.tsx';
import { createSegmentMetadata } from '../../drawer/components/SegmentsList/utils/createSegmentMetadata.ts';
import { formatReachValue } from '../../drawer/components/SegmentsList/utils/formatReachValue.ts';
import { PredefinedChipIcon } from '../../icons/PredefinedChipIcon.tsx';
import { createBasicInfoPropsFromSegment } from '../../utils/createBasicInfoProps.ts';
import { getExpireSoonTitle } from '../../utils/getExpireSoonTitle.ts';
import {
  boxIconContainerStyles,
  closeIconStyles,
  containerStyles,
  headerRowStyles,
} from '../../utils/segmentListItemStyles.ts';

export type SegmentListItemProps = {
  segment: FormSegment;
  isEdit: boolean;
  onDelete: () => void;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};

export const SegmentListItem = ({ segment, isEdit, onDelete, regions, countries }: SegmentListItemProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { organizations } = useReferenceData();
  const metadata = useMemo(() => createSegmentMetadata(segment, t, segment.account_id || ''), [segment, t]);
  const hasMarketMetadata = metadata.regionCodes.length > 0 || metadata.countryCodes.length > 0;

  const isPredefinedSegment = segment.category === SegmentCategory.PREDEFINED;

  const basicInfoProps = useMemo(
    () =>
      createBasicInfoPropsFromSegment({
        segment,
        id: segment.id,
        reach: segment.reach,
        displayReach: formatReachValue(t, segment.reach),
        category: segment.category || '',
        organizationName: segment.account_id ? organizations[segment.account_id] : t('N / A'),
        providerName: null,
        isPredefinedSegment,
      }),
    [segment, t, organizations, isPredefinedSegment]
  );

  const checkIfSegmentIsExpired = useMemo(() => {
    return isSegmentExpired(segment);
  }, [segment]);

  const checkIfSegmentIsSoonToExpire = useMemo(() => {
    return isSegmentSoonToExpire(segment);
  }, [segment]);

  const renderCorrectIcon = () => {
    if (checkIfSegmentIsExpired) {
      return (
        <Tooltip title={t('Segment data is expired and cannot be used for audience creation')}>
          <WarningCircleIcon
            style={{ marginRight: theme.spacing(1.5) }}
            data-test="error-icon"
            color={theme.palette.error.main}
            size={20}
          />
        </Tooltip>
      );
    }
    if (checkIfSegmentIsSoonToExpire) {
      return (
        <Tooltip title={getExpireSoonTitle(t, daysToExpiration(segment.expiration_date) ?? 0)}>
          <WarningIcon
            style={{ marginRight: theme.spacing(1.5) }}
            data-test="warning-icon"
            color={theme.palette.warning.main}
            size={20}
          />
        </Tooltip>
      );
    }
    return (
      <CheckCircleIcon
        style={{ marginRight: theme.spacing(1.5) }}
        data-test="check-icon"
        color={theme.palette.success.main}
        size={20}
      />
    );
  };

  return (
    <Box sx={containerStyles} data-test="segment-item">
      <Box sx={headerRowStyles}>
        {renderCorrectIcon()}

        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          {segment.category === SegmentCategory.PREDEFINED && (
            <Box sx={{ minWidth: 'fit-content', display: 'inline-flex' }} role="img" aria-label="Predefined segment">
              <PredefinedChipIcon />
            </Box>
          )}
          <Typography
            variant="body1Bold"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {segment.name}
          </Typography>
        </Box>
      </Box>

      <BasicInfo {...basicInfoProps} />
      {hasMarketMetadata && (
        <MarketMetadata
          regionCodes={metadata.regionCodes}
          countryCodes={metadata.countryCodes}
          regions={regions}
          countries={countries}
          dataTest="criteria-segment-market-metadata"
        />
      )}
      {isEdit && (
        <Box sx={boxIconContainerStyles} className="icon-container">
          <IconButton size="small" onClick={onDelete} className="close-icon" sx={closeIconStyles}>
            <Remove data-test="close-icon" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
